import { FooterProps, Row } from 'react-table';

import { Icon } from 'app/components';

import { CountryFlag } from 'CountryFlag/CountryFlag';

import { WorldFleetTableRow } from './WorldFleetPage';
import { VesselTypesMap } from 'app/constants/_constants';

import { t } from 'i18next';
import clsx from 'clsx';
import { VesselTypeName } from 'app/types/types';
import styles from './WorldFleetPage.module.scss';

const getVesselCount = (
  fleet: {
    typeName: VesselTypeName;
    count: number;
  }[],
  typeName: string,
) => fleet.find((el) => el.typeName === typeName)?.count ?? 0;

const renderCell = (total: number | undefined, color?: string) => (
  <div className={styles.fleet_amount_cell}>
    <Icon name={'satellite-target'} size="16px" color={color} fillOpacity="1" />
    {typeof total === 'number' && total > 0 ? total.toLocaleString('ru-RU') : <>&mdash;</>}
  </div>
);

const renderFooter = (total: number, color?: string) => (
  <div className={clsx(styles.fleet_amount_cell, styles.strong)}>
    <Icon name={'satellite-target'} size="16px" color={color} fillOpacity="1" />
    {total > 0 ? total.toLocaleString('ru-Ru') : <>&mdash;</>}
  </div>
);

const createSortFunction =
  (typeName: string) => (rowA: { original: WorldFleetTableRow }, rowB: { original: WorldFleetTableRow }) => {
    const totalA = getVesselCount(rowA.original.fleet, typeName);
    const totalB = getVesselCount(rowB.original.fleet, typeName);
    return totalA - totalB;
  };

export const columns = [
  {
    Header: 'Flag',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => (
      <>
        {countryRow.countryName ? (
          <>
            {countryRow.countryCode && <CountryFlag countryCode={countryRow.countryCode.toLowerCase()} />}
            {countryRow.countryName}
          </>
        ) : (
          <>{t('Others')}</>
        )}
      </>
    ),
    Footer: () => {
      return <div className={clsx(styles.fleet_amount_cell, styles.strong)}>{t('Total')}</div>;
    },
  },
  {
    Header: 'VesselTypeCargoPlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Cargo');
      return renderCell(total, VesselTypesMap.get('Cargo')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Cargo'), 0);
      return renderFooter(total, VesselTypesMap.get('Cargo')?.color);
    },
    sortType: createSortFunction('Cargo'),
    canSort: true,
  },
  {
    Header: 'VesselTypePassengerPlural',

    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Passenger');
      return renderCell(total, VesselTypesMap.get('Passenger')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Passenger'), 0);
      return renderFooter(total, VesselTypesMap.get('Passenger')?.color);
    },
    sortType: createSortFunction('Passenger'),
    canSort: true,
  },
  {
    Header: 'VesselTypeSpeedPlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Speed');
      return renderCell(total, VesselTypesMap.get('Speed')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Speed'), 0);
      return renderFooter(total, VesselTypesMap.get('Speed')?.color);
    },
    sortType: createSortFunction('Speed'),
    canSort: true,
  },
  {
    Header: 'VesselTypePleasurePlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Pleasure');
      return renderCell(total, VesselTypesMap.get('Pleasure')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Pleasure'), 0);
      return renderFooter(total, VesselTypesMap.get('Pleasure')?.color);
    },
    sortType: createSortFunction('Pleasure'),
    canSort: true,
  },
  {
    Header: 'VesselTypeTankerPlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Tanker');
      return renderCell(total, VesselTypesMap.get('Tanker')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Tanker'), 0);
      return renderFooter(total, VesselTypesMap.get('Tanker')?.color);
    },
    sortType: createSortFunction('Tanker'),
    canSort: true,
  },
  {
    Header: 'VesselTypeFishingPlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Fishing');
      return renderCell(total, VesselTypesMap.get('Fishing')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Fishing'), 0);
      return renderFooter(total, VesselTypesMap.get('Fishing')?.color);
    },
    sortType: createSortFunction('Fishing'),
    canSort: true,
  },
  {
    Header: 'VesselTypeSpecialTugPlural',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'TugOrSpecialCraft');
      return renderCell(total, VesselTypesMap.get('TugOrSpecialCraft')?.color);
    },

    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'TugOrSpecialCraft'), 0);
      return renderFooter(total, VesselTypesMap.get('TugOrSpecialCraft')?.color);
    },
    sortType: createSortFunction('TugOrSpecialCraft'),
    canSort: true,
  },
  {
    Header: 'VesselTypeOther',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      const total = getVesselCount(countryRow.fleet, 'Other');
      return renderCell(total, VesselTypesMap.get('Other')?.color);
    },
    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + getVesselCount(cur.original?.fleet, 'Other'), 0);
      return renderFooter(total, VesselTypesMap.get('Other')?.color);
    },
    sortType: createSortFunction('Other'),
    canSort: true,
  },
  {
    Header: 'Proportion',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      return <div className={styles.strong}>{parseFloat(countryRow.percentage?.toFixed(2))}%</div>;
    },
    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;

      const total = rows.reduce((acc, cur) => acc + cur.original.percentage, 0);
      return <div className={styles.strong}> {total > 99.8 ? 100 : total.toFixed(2)}%</div>;
    },
    sortType: (rowA: { original: WorldFleetTableRow }, rowB: { original: WorldFleetTableRow }) => {
      const totalA = rowA.original.percentage || 0;
      const totalB = rowB.original.percentage || 0;
      return totalA - totalB;
    },
    canSort: true,
  },

  {
    Header: 'Total',
    Cell: ({ row: { original: countryRow } }: { row: Row<WorldFleetTableRow> }) => {
      return (
        <div className={styles.strong}>
          {typeof countryRow.total === 'number' ? countryRow.total.toLocaleString('ru-RU') : <>&mdash;</>}
        </div>
      );
    },
    Footer: (props: FooterProps<WorldFleetTableRow>) => {
      const { rows } = props;
      const total = rows.reduce((acc, cur) => acc + cur.original.total, 0);

      return <div className={styles.strong}>{total > 0 ? total.toLocaleString('ru-Ru') : <>&mdash;</>}</div>;
    },
    sortBy: 'total',
    accessor: 'total',
    canSort: true,
  },
];
